import useSWR from "swr";
import { Pagination, Card } from "antd";
import { useState, useMemo } from "react";
import { fetcher } from "../util/axiosConfig";
import ActivityLogsFilter from "./ActivityLogsFilter";
import useTablePagination from "../hooks/useTablePagination";
import { Loader, MoreVertical } from "lucide-react";

interface Log {
  actorType: string;
  entityType: string;
  source: string;
  timestamp: string;
  action: string;
  metadata: {
    actorName: string;
    actorEmail: string
  }
}

function ActivityLogs() {
  const [filters, setFilters] = useState<any>();
  const {pagination, setPagination} = useTablePagination("activity-logs");

  const searchParams = `${new URLSearchParams({
    ...filters,
    page: pagination.page || 1
  } as Record<string, string>).toString()}`

  const { data, isLoading } = useSWR(
    filters
      ? `/newAdmin/logs/?${searchParams}`
      : `/newAdmin/logs/?page=${pagination.page || 1}`
    , fetcher);

  const logs: Log[] = useMemo(() => {
    return data?.data || []
  }, [data]);

  const summary = useMemo(() => {
    return {
      page: data?.data?.page || 0,
      pages: data?.data?.pages || 0,
      total: data?.data?.total || 0
    }
  }, [data]);

  return (
    <div>
      <Card title={
        <div className="flex justify-between items-center">
          <h3>Activity Logs</h3>
          <ActivityLogsFilter onChange={(vals) => setFilters(vals)} />
        </div>
      }>
        <LogOverview isLoading={isLoading} logs={logs} />
        <div className="flex justify-end mt-4">
          {!isLoading && (
            <Pagination
              defaultCurrent={pagination.current}
              total={summary.total}
              onChange={(page, pageSize) => setPagination({
                ...pagination,
                page: page,
                current: page,
                pageSize
              })}
            />
          )}
        </div>
      </Card>
    </div>
  )
}

export default ActivityLogs;

interface LogOverviewProps {
  logs: Log[];
  isLoading: boolean;
}

function LogOverview({ logs, isLoading }: LogOverviewProps) {
  return (<div>
    {isLoading && (<div className="flex justify-center items-center py-10">
      <Loader size={30} />
    </div>
    )}
    {!isLoading && logs.length < 1 && (
      <p className="text-center text-muted-foreground">
        No Logs Found
      </p>
    )}
    {!isLoading && logs.map((log, index) => (
      <LogRow key={index} {...log} />
    )) }
  </div>)
}

interface Log {
  actorType: string;
  entityType: string;
  source: string;
  timestamp: string;
  action: string;
  metadata: {
    actorName: string;
    actorEmail: string
  }
}

function LogRow(props: Log) {
  const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'gd':
        return '#096dd9';
      case 'bm':
        return '#fd7e14';
      case 'api':
        return '#faad14';
      default:
        return '#096dd9';
    }
  }

  return (<div className="flex items-center flex-nowrap gap-2">
    {/* <span className="text-sm font-medium"><MoreVertical /></span> */}
    <span className="text-base font-bold uppercase whitespace-nowrap" style={{ color: getPlatformColor(props.source) }}>{props.source}</span>
    <span className="text-sm text-muted-foreground whitespace-nowrap lowercase">actor=<b className="font-bold">{props.metadata.actorName}</b></span>
    <span className="text-sm text-muted-foreground whitespace-nowrap lowercase">type=<b className="font-bold">{props.actorType}</b></span>
    <span className="text-sm text-muted-foreground whitespace-nowrap lowercase">entity=<b className="font-bold">{props.entityType}</b></span>
    <span className="text-sm text-muted-foreground whitespace-nowrap lowercase">datetime=<b className="font-bold">{props.timestamp}</b></span>
    <span className="text-sm text-muted-foreground whitespace-nowrap lowercase">action=<b className="font-bold">{props.action}</b></span>
  </div>)
}